// A message a session or an agent sent to an agent for a person is stored as the PERSON's row; its
// `handoff` card names the sender, and the chat draws the row as the sender's. Pure: no store.
import type { HandoffCard } from "$lib/core/types.js";
import type { MessageEdge } from "$lib/plugins/agents/message-edge.js";

export interface RelayedSource {
  kind: "agent" | "session";
  /** Never a raw id. */
  name: string;
  signatures: string[];
  agentId: string;
  cyboId?: string;
  provider?: string;
  /** False when nothing named the source. */
  known: boolean;
}

export interface RelayedSourceFacts {
  agents: ReadonlyArray<{ agentId: string; cyboId?: string | null; provider?: string | null }>;
  cybos: ReadonlyArray<{ id: string; name: string }>;
  /** sessionOwnName: the viewer's alias, else the shared name. */
  ownName: (session: { agentId: string }) => string | undefined;
  /** The relay's record of this message (the card's handoffId), when this client holds it. */
  edge?: MessageEdge;
  providerLabel: (provider: string) => string | undefined;
}

export const UNKNOWN_SESSION = "Unknown session";
export const UNKNOWN_AGENT = "Unknown agent";
export const RELAYED_CHIP = { agent: "Agent", session: "Session" } as const;

const clean = (v: string | null | undefined): string | undefined => v?.trim() || undefined;

// A name that only repeats the id names nothing.
const named = (v: string | null | undefined, id: string): string | undefined => {
  const name = clean(v);
  return name && name !== id && name !== id.slice(0, 8) ? name : undefined;
};

// What the card, the viewer's roster and the message's edge each say about the source.
function sourceFacts(card: HandoffCard, facts: RelayedSourceFacts) {
  const agentId = card.sourceAgentId;
  const row = facts.agents.find((a) => a.agentId === agentId);
  const from = facts.edge?.from;
  const edgeCyboId = from?.kind === "agent" ? from.id : undefined;
  return {
    row,
    cyboId: clean(card.sourceCyboId) ?? clean(row?.cyboId) ?? edgeCyboId,
    provider: clean(row?.provider) ?? clean(card.sourceProvider) ?? clean(from?.provider),
    stamped: [named(card.sourceName, agentId), named(from?.name, from?.id ?? agentId)],
  };
}

/** Who sent a relayed row: the card's own names, else the viewer's roster and the message's edge. */
export function relayedSource(card: HandoffCard, facts: RelayedSourceFacts): RelayedSource {
  const agentId = card.sourceAgentId;
  const { row, cyboId, provider, stamped } = sourceFacts(card, facts);
  const kind = card.sourceKind ?? (cyboId ? "agent" : "session");
  const live =
    kind === "agent"
      ? clean(facts.cybos.find((c) => c.id === cyboId)?.name)
      : clean(facts.ownName(row ?? { agentId }));
  const name = live ?? stamped[0] ?? stamped[1] ?? providerName(kind, provider, facts);
  return {
    kind,
    name: name ?? UNKNOWN[kind],
    signatures: unique([live, ...stamped]),
    agentId,
    ...(kind === "agent" && cyboId ? { cyboId } : {}),
    ...(provider ? { provider } : {}),
    known: !!name,
  };
}

const UNKNOWN = { agent: UNKNOWN_AGENT, session: UNKNOWN_SESSION } as const;

// A session nothing named is called by what it runs ("Claude session"); an agent never is.
function providerName(
  kind: RelayedSource["kind"],
  provider: string | undefined,
  facts: RelayedSourceFacts,
): string | undefined {
  const label = kind === "session" && provider ? facts.providerLabel(provider) : undefined;
  return label ? `${label} session` : undefined;
}

const unique = (names: Array<string | undefined>): string[] => [
  ...new Set(names.filter((n): n is string => !!n)),
];

/** The text without the sender's own "<name>: " signature, which the header already says. */
export function relayedText(text: string, source: Pick<RelayedSource, "signatures">): string {
  for (const name of source.signatures) {
    const prefix = `${name}:`;
    if (!text.startsWith(prefix)) continue;
    const rest = text.slice(prefix.length);
    if (/^[ \t]*\S/.test(rest) && /^[ \t]/.test(rest)) return rest.replace(/^[ \t]+/, "");
    if (/^[ \t]*\r?\n\s*\S/.test(rest)) return rest.replace(/^\s+/, "");
  }
  return text;
}

/** The hover line: who sent it for whom, then the chain facts for whoever debugs it. */
export function relayedTitle(
  card: Pick<HandoffCard, "chainId" | "hop" | "limit">,
  source: Pick<RelayedSource, "name" | "kind" | "known">,
  person: string | undefined,
): string {
  const a = source.kind === "agent" ? "an agent" : "a session";
  const sender = source.known ? `${source.name} (${a})` : `${a[0].toUpperCase()}${a.slice(1)}`;
  const who = person ? ` on behalf of ${person}` : "";
  return `${sender} sent this message${who}. Chain ${card.chainId.slice(0, 8)}, hop ${card.hop} of ${card.limit}.`;
}

/** The source of a relayed row, or null for every other message (what grouping compares). */
export function relayedBy(message: object & { card?: { kind?: string } | null }): string | null {
  const card = message.card;
  if (card?.kind !== "handoff") return null; // null: an ordinary message
  return (card as Partial<HandoffCard>).sourceAgentId ?? "";
}
